import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Bell, BellOff, Globe, CalendarDays, Moon } from 'lucide-react';
import { SuitFor } from './Suit';
import { useT } from '../i18n/LanguageContext';

export const BT_SCHEDULE = [
  { id: 'BT1', label: 'BT1', hourUTC: 19, suit: 'heart'   },
  { id: 'BT2', label: 'BT2', hourUTC: 13, suit: 'diamond' },
];

const MS_DAY = 24 * 60 * 60 * 1000;
const COOLDOWN_DAYS = 2; // 48h cooldown between BT events
const NOTIF_LEAD_MS = 5 * 60 * 1000; // 5 minutes before

const STORAGE_REMINDERS = 'hoc.btReminders';
const STORAGE_ANCHOR = 'hoc.btAnchor'; // ISO YYYY-MM-DD (UTC) of a known BT day

function todayUTC(from = new Date()) {
  return new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
}

function toIsoDate(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function parseIsoDateUTC(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const [y, m, d] = iso.split('-').map((v) => parseInt(v, 10));
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

// Smallest BT datetime (UTC hour `hourUTC`) that is strictly after `from`,
// aligned to the 48h cadence from `anchor` (which marks a known BT day).
function nextBTOccurrence(hourUTC, anchor, from = new Date()) {
  const start = todayUTC(from);
  let cand = new Date(start);
  for (let i = 0; i < 30; i++) {
    const daysDiff = Math.round((cand.getTime() - anchor.getTime()) / MS_DAY);
    if (daysDiff >= 0 && daysDiff % COOLDOWN_DAYS === 0) {
      const occur = new Date(Date.UTC(cand.getUTCFullYear(), cand.getUTCMonth(), cand.getUTCDate(), hourUTC));
      if (occur.getTime() > from.getTime()) return occur;
    }
    cand = new Date(cand.getTime() + MS_DAY);
  }
  return null;
}

function isBTDay(dateUTC, anchor) {
  const daysDiff = Math.round((dateUTC.getTime() - anchor.getTime()) / MS_DAY);
  return daysDiff >= 0 && daysDiff % COOLDOWN_DAYS === 0;
}

function formatDuration(ms) {
  if (ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return d > 0 ? `${d}d ${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`;
}

function loadReminders() {
  try { return JSON.parse(localStorage.getItem(STORAGE_REMINDERS) || '{}') || {}; } catch (e) { return {}; }
}
function saveReminders(obj) {
  try { localStorage.setItem(STORAGE_REMINDERS, JSON.stringify(obj)); } catch (e) { /* ignore */ }
}
function loadAnchor() {
  try {
    const raw = localStorage.getItem(STORAGE_ANCHOR);
    if (raw) {
      const d = parseIsoDateUTC(raw);
      if (d) return d;
    }
  } catch (e) { /* ignore */ }
  return todayUTC();
}
function saveAnchor(d) {
  try { localStorage.setItem(STORAGE_ANCHOR, toIsoDate(d)); } catch (e) { /* ignore */ }
}

export default function BearHuntClock() {
  const { t } = useT();
  const [now, setNow] = useState(() => new Date());
  const [reminders, setReminders] = useState(() => loadReminders());
  const [firedIds, setFiredIds] = useState(() => new Set());
  const [anchor, setAnchor] = useState(() => loadAnchor());
  const [notifPermission, setNotifPermission] = useState(() =>
    (typeof Notification !== 'undefined' ? Notification.permission : 'default')
  );

  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => { saveAnchor(anchor); }, [anchor]);

  // Fire notifications 5 min before each BT
  useEffect(() => {
    const t0 = now.getTime();
    BT_SCHEDULE.forEach((bt) => {
      if (!reminders[bt.id]) return;
      const next = nextBTOccurrence(bt.hourUTC, anchor, now);
      if (!next) return;
      const diff = next.getTime() - t0;
      const key = `${bt.id}-${toIsoDate(next)}`;
      if (diff > 0 && diff <= NOTIF_LEAD_MS && !firedIds.has(key)) {
        setFiredIds((prev) => { const s = new Set(prev); s.add(key); return s; });
        const title = t('clock.notifTitle');
        const body = t('clock.notifBody', { bt: bt.label });
        try {
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            new Notification(title, { body, tag: key });
          }
        } catch (e) { /* ignore */ }
        try {
          const banner = document.createElement('div');
          banner.textContent = `${title} — ${body}`;
          banner.style.cssText =
            'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:9999;background:#0d0d0d;color:#fff;border:1px solid #c92a2a;padding:12px 18px;border-radius:10px;font-weight:600;box-shadow:0 10px 30px rgba(0,0,0,0.35);';
          document.body.appendChild(banner);
          setTimeout(() => banner.remove(), 8000);
        } catch (e) { /* ignore */ }
      }
    });
  }, [now, reminders, firedIds, t, anchor]);

  const toggleReminder = async (id) => {
    const next = { ...reminders, [id]: !reminders[id] };
    setReminders(next);
    saveReminders(next);
    if (next[id] && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        const p = await Notification.requestPermission();
        setNotifPermission(p);
      } catch (e) { /* ignore */ }
    }
  };

  const localZone = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
  }, []);
  const localTimeStr = useMemo(
    () => now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
    [now]
  );
  const utcTimeStr = useMemo(
    () => `${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')}:${String(now.getUTCSeconds()).padStart(2, '0')}`,
    [now]
  );

  const todayIsBT = isBTDay(todayUTC(now), anchor);
  const anchorIso = toIsoDate(anchor);

  // Local label for the next BT occurrence of each schedule
  const btRows = BT_SCHEDULE.map((bt) => {
    const next = nextBTOccurrence(bt.hourUTC, anchor, now);
    const diff = next ? next.getTime() - now.getTime() : null;
    return {
      ...bt,
      next,
      diff,
      localLabel: next
        ? next.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
        : '',
      utcLabel: `${String(bt.hourUTC).padStart(2, '0')}:00`,
      soon: diff !== null && diff <= NOTIF_LEAD_MS,
    };
  });

  return (
    <section className="rounded-xl bg-white border border-neutral-200 shadow-[0_6px_18px_rgba(0,0,0,0.05)] overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-[#c92a2a] via-[#e03131] to-[#c92a2a]" />
      <div className="px-5 py-3 border-b border-neutral-100 bg-neutral-50/60 flex items-center justify-between">
        <h2 className="font-serif font-bold text-neutral-900 flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#c92a2a]" />
          {t('clock.title')}
        </h2>
        <span className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider inline-flex items-center gap-1">
          <Globe className="w-3 h-3" />
          {localZone || 'Local'}
        </span>
      </div>

      {/* Current time strip */}
      <div className="px-5 py-3 grid grid-cols-2 gap-3 text-center border-b border-neutral-100">
        <div className="rounded-lg bg-neutral-50 py-2">
          <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">{t('clock.yourTime')}</div>
          <div className="font-mono text-lg font-bold text-neutral-900 tabular-nums">{localTimeStr}</div>
        </div>
        <div className="rounded-lg bg-neutral-900 text-white py-2">
          <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">{t('clock.utc')}</div>
          <div className="font-mono text-lg font-bold text-[#ff8787] tabular-nums">{utcTimeStr}</div>
        </div>
      </div>

      {/* Cooldown / Anchor date */}
      <div className="px-5 py-3 border-b border-neutral-100 bg-neutral-50/40">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarDays className="w-4 h-4 text-[#c92a2a] flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider">
                {t('clock.anchorLabel')}
              </div>
              <div className="text-[11px] text-neutral-500">{t('clock.cooldown')}</div>
            </div>
          </div>
          <input
            type="date"
            value={anchorIso}
            onChange={(e) => {
              const d = parseIsoDateUTC(e.target.value);
              if (d) setAnchor(d);
            }}
            className="px-2.5 py-1.5 rounded-md border-2 border-neutral-200 bg-white text-neutral-900 text-sm font-semibold focus:outline-none focus:border-[#c92a2a]"
          />
        </div>
        {!todayIsBT && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#c92a2a] bg-[#fff5f5] border border-[#c92a2a]/40 rounded-full px-2.5 py-1">
            <Moon className="w-3 h-3" />
            {t('clock.restDay')}
          </div>
        )}
      </div>

      {/* BT countdowns */}
      <div className="px-5 py-4 space-y-3">
        {btRows.map((bt) => {
          const on = !!reminders[bt.id];
          return (
            <div key={bt.id} className={`rounded-lg border-2 p-3 flex items-center gap-3 ${bt.soon ? 'border-[#c92a2a] bg-[#fff5f5]' : 'border-neutral-200 bg-white'}`}>
              <div className={`w-10 h-10 rounded-md inline-flex items-center justify-center flex-shrink-0 ${bt.suit === 'heart' ? 'bg-[#c92a2a] text-white' : 'bg-[#0d0d0d] text-[#ff6b6b]'}`}>
                <SuitFor name={bt.suit} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-lg font-bold text-neutral-900">{bt.label}</span>
                  <span className="text-[11px] font-semibold text-neutral-500">{bt.utcLabel} UTC</span>
                  {bt.localLabel && (
                    <>
                      <span className="text-[11px] text-neutral-500">·</span>
                      <span className="text-[11px] font-semibold text-neutral-700">{bt.localLabel}</span>
                    </>
                  )}
                </div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">{t('clock.nextIn')}</span>
                  <span className={`font-mono font-bold tabular-nums ${bt.soon ? 'text-[#c92a2a] text-xl' : 'text-neutral-900 text-lg'}`}>
                    {bt.diff !== null ? formatDuration(bt.diff) : '—'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggleReminder(bt.id)}
                aria-label={on ? 'Disable reminder' : 'Enable reminder'}
                className={`flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-md border-2 transition-colors ${
                  on
                    ? 'bg-[#c92a2a] text-white border-[#c92a2a]'
                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-[#c92a2a]'
                }`}
                title={on ? t('clock.remindOn') : t('clock.remind')}
              >
                {on ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </button>
            </div>
          );
        })}
        {notifPermission === 'denied' && (
          <p className="text-[11px] text-[#c92a2a] italic">{t('clock.notifDenied')}</p>
        )}
      </div>
    </section>
  );
}
